import React from 'react'
import { useLocation } from 'react-router-dom'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'

export type DevStepName = string

export interface RegisterDevState {
  /** Whether the panel UI should be visible */
  uiVisible: boolean
  /** Whether dev mode is enabled */
  enabled: boolean
  /** Bypass form validation */
  bypassValidation: boolean
  /** Last selected step from rolodex - used as default starting value */
  lastSelectedStep: DevStepName
  /** Container selector for rendering the panel */
  panelContainerSelector: string
}

const DEFAULT_STATE: RegisterDevState = {
  uiVisible: false,
  enabled: false,
  bypassValidation: false,
  lastSelectedStep: 'accountLookup',
  panelContainerSelector: 'body',
}

const STORAGE_KEY = 'mk-register-dev-state'

function readPersisted(): RegisterDevState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_STATE
    const parsed = JSON.parse(raw)
    return { ...DEFAULT_STATE, ...parsed }
  } catch (_) {
    return DEFAULT_STATE
  }
}

function writePersisted(state: RegisterDevState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (err) {
    // ignore persistence errors (e.g., disabled storage)
  }
}

export interface DevUiContextType extends RegisterDevState {
  setEnabled: (enabled: boolean) => void
  setBypassValidation: (bypass: boolean) => void
  setPanelContainerSelector: (selector: string) => void
  setLastSelectedStep: (step: DevStepName) => void
}

const DevUiContext = React.createContext<DevUiContextType | undefined>(undefined)

export const DevUiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const [state, setState] = React.useState<RegisterDevState>(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const hasFlag = urlParams.has('regDev')

    // Always try to read the last selected step from localStorage
    const lastSelectedStep =
      DataStore.getItem<string>(EMkStorageKeys.DEV_UI_LAST_SELECTED_STEP, {
        store: localStorage,
        defaultValue: 'accountLookup',
      }) || 'accountLookup'

    if (!hasFlag) {
      // No regDev parameter - use default state
      return {
        ...DEFAULT_STATE,
        lastSelectedStep,
      }
    }

    // Has regDev parameter - read from localStorage and apply settings
    const initial = readPersisted()
    const regDevValue = urlParams.get('regDev')
    const enabled = regDevValue === '1'

    return {
      ...DEFAULT_STATE,
      ...initial,
      enabled,
      uiVisible: enabled,
      lastSelectedStep: initial.lastSelectedStep || lastSelectedStep,
    }
  })

  // Only persist state when dev mode is actually enabled
  React.useEffect(() => {
    if (state.enabled) {
      writePersisted(state)
    }
  }, [state])

  // Cleanup localStorage when dev mode is disabled
  React.useEffect(() => {
    if (!state.enabled) {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [state.enabled])

  // React to URL changes immediately (no reload) to toggle/clear dev state
  React.useEffect(() => {
    const params = new URLSearchParams(location.search)
    const hasFlag = params.has('regDev')

    if (!hasFlag) {
      // Check if we have a persisted dev state that should remain active
      const persisted = readPersisted()
      if (persisted.enabled) {
        // Keep dev mode active even without URL parameter
        setState((s) => ({ ...s, uiVisible: true, enabled: true }))
        return
      }

      // Clear dev state and remove from localStorage when no regDev parameter
      setState((s) => ({ ...s, uiVisible: false, enabled: false }))
      localStorage.removeItem(STORAGE_KEY)
      return
    }

    const regDevValue = params.get('regDev')
    const enabledNow = regDevValue === '1'

    setState((s) => ({
      ...s,
      uiVisible: enabledNow,
      enabled: enabledNow,
      bypassValidation: enabledNow || s.bypassValidation,
    }))
  }, [location.search])

  const contextValue = React.useMemo<DevUiContextType>(
    () => ({
      ...state,
      setEnabled: (isEnabled) => setState((s) => ({ ...s, enabled: isEnabled })),
      setBypassValidation: (bypass) => setState((s) => ({ ...s, bypassValidation: bypass })),
      setPanelContainerSelector: (selector) => setState((s) => ({ ...s, panelContainerSelector: selector || 'body' })),
      setLastSelectedStep: (step) => {
        setState((s) => ({ ...s, lastSelectedStep: step }))
        DataStore.setItem(EMkStorageKeys.DEV_UI_LAST_SELECTED_STEP, step, { store: localStorage })
      },
    }),
    [state],
  )

  return <DevUiContext.Provider value={contextValue}>{children}</DevUiContext.Provider>
}

export const useDevUi = (): DevUiContextType => {
  const ctx = React.useContext(DevUiContext)
  if (ctx) return ctx

  // Check if we're in a dev context (has regDev parameter)
  const hasRegDev = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('regDev')

  if (!hasRegDev) {
    // No dev context - return default state with no-op functions
    return {
      ...DEFAULT_STATE,
      setEnabled: () => {},
      setBypassValidation: () => {},
      setPanelContainerSelector: () => {},
      setLastSelectedStep: () => {},
    }
  }

  // Has dev context - read from localStorage
  const persisted = readPersisted()
  return {
    ...persisted,
    setEnabled: () => {},
    setBypassValidation: () => {},
    setPanelContainerSelector: () => {},
    setLastSelectedStep: () => {},
  }
}

// Backward-compatible export name
export { DevUiProvider as RegisterDevProvider }
export type RegisterDevContextType = DevUiContextType
export const RegisterDevContext = DevUiContext
export const useRegisterDev = useDevUi
